// Resolve the user's current TMS organisation id straight from the Auth0
// access-token claims. This is a stop-gap — once we add per-org switching
// support, this helper goes away in favour of `useCurrentOrganisationId()`
// from the shared account store (`@my/platform/account`).
//
// Claim shape (mirrors My.Tms.Old/utils/roles.ts):
//   `https://my/app_roles`: array of `tms:<role>:<organisationId>` strings
//      where `<organisationId>` is the GUID with dashes stripped.

import { getSupportOrganisationId } from '@my/platform/support';

const TMS_APP = 'tms';

/** Re-insert the standard 8-4-4-4-12 dashes into a 32-char hex string. */
function reinsertGuidDashes(stripped: string): string {
  if (stripped.length !== 32) return stripped;
  return (
    `${stripped.slice(0, 8)}-${stripped.slice(8, 12)}-${stripped.slice(12, 16)}-` +
    `${stripped.slice(16, 20)}-${stripped.slice(20)}`
  );
}

/**
 * Returns the first TMS organisation id found in the user's `app_roles`
 * claim, or `null` if none. Most TMS users have exactly one org so the
 * "first match" heuristic is sufficient until tenant switching lands.
 */
export function getCurrentOrganisationId(user: unknown): string | null {
  // A Stratum support session comes first, and has to: a support token carries
  // no `app_roles` claim at all — that is what keeps it constant-size across
  // every account on the platform — so there is no organisation in it to read.
  // The organisation is the one picked in the support bar instead.
  const supportOrganisationId = getSupportOrganisationId();
  if (supportOrganisationId) return supportOrganisationId;

  if (!user || typeof user !== 'object') return null;
  const claims = user as Record<string, unknown>;
  const appRoles = claims['https://my/app_roles'];
  if (!Array.isArray(appRoles)) return null;

  for (const raw of appRoles) {
    if (typeof raw !== 'string') continue;
    const parts = raw.split(':');
    if (parts[0]?.toLowerCase() !== TMS_APP) continue;
    const orgIdSegment = parts[2];
    if (!orgIdSegment) continue;
    return reinsertGuidDashes(orgIdSegment.toLowerCase());
  }
  return null;
}
